import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ReferenceLine,
} from 'recharts';
import { useAuth0 } from '@auth0/auth0-react';
import {
  fetchRevenue,
  RevenuePayload,
  fetchWealth,
  WealthPayload,
  fetchGrowth,
  GrowthPayload,
  fetchLlcSales,
  LlcSalesPayload,
  fetchOperations,
  OperationsPayload,
  fetchRetention,
  RetentionPayload,
  ApiAuthError,
  ApiForbiddenError,
} from '../api';
import { Card, CardTitle, usd } from '../components/ui';
import FeeBreakdownCard from '../components/FeeBreakdownCard';
import { KpiChip } from '../components/KpiCatalog';
import { kpiThemes, hasData } from '../data/kpiCatalog';
import { GRAINS, Grain } from '../lib/grains';
import { RANGES, RangeKey, rangeToDates } from '../lib/ranges';
import { isSilentAuthError } from '../lib/auth';

const BRAND = '#ba9965';
const GOLD = [
  '#ba9965',
  '#ae905c',
  '#d1b88d',
  '#c2a274',
  '#977b4a',
  '#a49b94',
  '#c9b494',
  '#8c7860',
];
const GREEN = '#0a8754';
const RED = '#c53030';
const SERIF = "'Spectral', Georgia, serif";
const AXIS = 'var(--color-on-surface-quaternary)';
const GRID = 'var(--color-border)';

const axisTick = { fill: AXIS, fontSize: 10 };
const cleanAxis = { axisLine: false as const, tickLine: false as const };
const gridProps = { vertical: false, stroke: GRID, strokeDasharray: '2 6' };

const tooltipStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 10,
  fontSize: 12,
  color: 'var(--color-on-surface)',
  boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
  padding: '8px 10px',
};

const fmtUsd = (v: any) => usd(Number(v));
const fmtUsdCompact = (v: any) => usd(Number(v), true);
// Wealth measures are genuinely nullable (no custody snapshot for a bucket, or an
// amount that failed to parse upstream). Number(null) is 0, so formatting a null
// through fmtUsd would print "$0" — a real-looking figure for missing data.
const fmtUsdOrDash = (v: any) => (v == null ? '—' : usd(Number(v)));
// Token quantities are not dollars: usd() rounds to whole units, which would
// render a 0.62 BTC position as "$1".
const fmtQty = (v: any) =>
  v == null ? '—' : Intl.NumberFormat('en-US', { maximumFractionDigits: 8 }).format(Number(v));

// Outflows are plotted below the axis as negative values, so tooltips and bar
// labels on the flow charts must report the magnitude, not the plotted sign.
const fmtUsdAbs = (v: any) => usd(Math.abs(Number(v)));
const fmtUsdAbsCompact = (v: any) => usd(Math.abs(Number(v)), true);

// canon_clean.anchorage_custody_scope's raw `custodian` values are free-text API
// strings ('anchorage', 'coinbase', 'gemini', 'unknown') — display case, not
// title case. 'unknown' specifically means the advisor_reported scope (a
// manually-tracked model/strategy position, not an unresolved custodian), so it
// gets its own label rather than rendering the literal word "unknown".
const CUSTODIAN_LABELS: Record<string, string> = {
  anchorage: 'Anchorage',
  coinbase: 'Coinbase',
  gemini: 'Gemini',
};
const custodianLabel = (custodian: string, custodyScope: string) =>
  custodyScope === 'advisor_reported'
    ? 'Advisor Reported'
    : (CUSTODIAN_LABELS[custodian.trim().toLowerCase()] ?? custodian);

const FORBIDDEN_MESSAGE =
  "Your account isn't authorized to view this dashboard. It requires a Digital Family Office (@dag.com) " +
  'account with KPI dashboard access — sign in with one, or ask an admin to grant access.';

export const DashboardPage: React.FC<{ area: string }> = ({ area }) => {
  const [grain, setGrain] = useState<Grain>('month');
  const [from, setFrom] = useState(() => rangeToDates('90d').from);
  const [to, setTo] = useState(() => rangeToDates('90d').to);
  const [preset, setPreset] = useState<RangeKey | null>('90d');
  const [data, setData] = useState<RevenuePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Shares the Revenue page's grain and range but loads on its own, so the slower
  // /llc-sales query neither delays nor fails the rest of the page.
  const [llcData, setLlcData] = useState<LlcSalesPayload | null>(null);
  const [llcLoading, setLlcLoading] = useState(true);
  const [llcError, setLlcError] = useState<string | null>(null);
  // Keyed by area (rather than a plain boolean) so that switching areas clears
  // forbidden synchronously during render — a boolean reset in a useEffect only
  // takes effect after React paints, causing a one-frame flash of the forbidden
  // screen for the new area.
  const [forbiddenArea, setForbiddenArea] = useState<string | null>(null);
  const forbidden = forbiddenArea === area;
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // getAccessTokenSilently throws login_required/consent_required/interaction_required
  // when the session can't be renewed without user interaction (e.g. no SSO session,
  // or a browser blocking the renewal iframe); the API itself throws ApiAuthError on
  // a 401. Both mean "sign in again," not "the data failed to load" — redirect
  // instead of surfacing an error banner the user has no way to act on.
  //
  // ApiForbiddenError (403) is deliberately NOT included here: it means the token
  // is valid but the account isn't @dag.com, so redirecting to login would just
  // loop back to the same 403 — that case is handled separately as `forbidden`.
  const needsReauth = (e: unknown) => isSilentAuthError(e) || e instanceof ApiAuthError;

  const [wealthGrain, setWealthGrain] = useState<Grain>('week');
  const [wealthFrom, setWealthFrom] = useState(() => rangeToDates('90d').from);
  const [wealthTo, setWealthTo] = useState(() => rangeToDates('90d').to);
  const [wealthPreset, setWealthPreset] = useState<RangeKey | null>('90d');
  const [wealthData, setWealthData] = useState<WealthPayload | null>(null);
  const [wealthLoading, setWealthLoading] = useState(true);
  const [wealthError, setWealthError] = useState<string | null>(null);

  // 'all' by default: the growth series is cumulative, so the whole history is
  // the natural first view (unlike the flow charts, where 90d is).
  const [growthGrain, setGrowthGrain] = useState<Grain>('month');
  const [growthFrom, setGrowthFrom] = useState(() => rangeToDates('all').from);
  const [growthTo, setGrowthTo] = useState(() => rangeToDates('all').to);
  const [growthPreset, setGrowthPreset] = useState<RangeKey | null>('all');
  const [growthData, setGrowthData] = useState<GrowthPayload | null>(null);
  const [growthLoading, setGrowthLoading] = useState(true);
  const [growthError, setGrowthError] = useState<string | null>(null);

  // No grain or range: ticket age is a snapshot of the open backlog right now.
  const [opsData, setOpsData] = useState<OperationsPayload | null>(null);
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsError, setOpsError] = useState<string | null>(null);

  // '90d' by default: at the time of writing we don't have a very long history of
  // the data needed to calculate retention stats, so start with a range we should have.
  const [retGrain, setRetGrain] = useState<Grain>('week');
  const [retFrom, setRetFrom] = useState(() => rangeToDates('90d').from);
  const [retTo, setRetTo] = useState(() => rangeToDates('90d').to);
  const [retPreset, setRetPreset] = useState<RangeKey | null>('90d');
  const [retData, setRetData] = useState<RetentionPayload | null>(null);
  const [retLoading, setRetLoading] = useState(true);
  const [retError, setRetError] = useState<string | null>(null);

  const applyRetPreset = (key: RangeKey) => {
    const r = rangeToDates(key);
    setRetFrom(r.from);
    setRetTo(r.to);
    setRetPreset(key);
  };

  const applyGrowthPreset = (key: RangeKey) => {
    const r = rangeToDates(key);
    setGrowthFrom(r.from);
    setGrowthTo(r.to);
    setGrowthPreset(key);
  };

  const applyPreset = (key: RangeKey) => {
    const r = rangeToDates(key);
    setFrom(r.from);
    setTo(r.to);
    setPreset(key);
  };

  const applyWealthPreset = (key: RangeKey) => {
    const r = rangeToDates(key);
    setWealthFrom(r.from);
    setWealthTo(r.to);
    setWealthPreset(key);
  };

  useEffect(() => {
    if (area !== 'Revenue') return;
    let active = true;
    setLoading(true);
    setForbiddenArea(null);
    const getToken = () => getAccessTokenSilently();
    fetchRevenue(grain, from, to, getToken)
      .then((d) => {
        if (active) {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => {
        if (!active) return;
        if (e instanceof ApiForbiddenError) {
          setForbiddenArea(area);
          return;
        }
        if (needsReauth(e)) {
          active = false;
          loginWithRedirect().catch(() => {
            setError('Authentication required. Please refresh the page.');
            setLoading(false);
          });
          return;
        }
        setError(String(e));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [grain, from, to, area, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (area !== 'Revenue') return;
    let active = true;
    setLlcLoading(true);
    setLlcError(null);
    const getToken = () => getAccessTokenSilently();
    fetchLlcSales(grain, from, to, getToken)
      .then((d) => {
        if (active) {
          setLlcData(d);
        }
      })
      .catch((e) => {
        if (!active) return;
        // The revenue fetch on the same page owns the forbidden page and the
        // re-login redirect; doing either here too would redirect twice. Still
        // set an error, or the section would sit on its skeleton.
        if (e instanceof ApiForbiddenError) {
          setLlcError('You do not have access to LLC sales.');
          return;
        }
        if (needsReauth(e)) {
          setLlcError('Authentication required. Please refresh the page.');
          return;
        }
        setLlcError(String(e));
      })
      .finally(() => active && setLlcLoading(false));
    return () => {
      active = false;
    };
  }, [grain, from, to, area, getAccessTokenSilently]);

  useEffect(() => {
    if (area !== 'Wealth & AUM') return;
    let active = true;
    setWealthLoading(true);
    setForbiddenArea(null);
    const getToken = () => getAccessTokenSilently();
    fetchWealth(wealthGrain, wealthFrom, wealthTo, getToken)
      .then((d) => {
        if (active) {
          setWealthData(d);
          setWealthError(null);
        }
      })
      .catch((e) => {
        if (!active) return;
        if (e instanceof ApiForbiddenError) {
          setForbiddenArea(area);
          return;
        }
        if (needsReauth(e)) {
          active = false;
          loginWithRedirect().catch(() => {
            setWealthError('Authentication required. Please refresh the page.');
            setWealthLoading(false);
          });
          return;
        }
        setWealthError(String(e));
      })
      .finally(() => active && setWealthLoading(false));
    return () => {
      active = false;
    };
  }, [wealthGrain, wealthFrom, wealthTo, area, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (area !== 'Growth & Acquisition') return;
    let active = true;
    setGrowthLoading(true);
    setForbiddenArea(null);
    const getToken = () => getAccessTokenSilently();
    fetchGrowth(growthGrain, growthFrom, growthTo, getToken)
      .then((d) => {
        if (active) {
          setGrowthData(d);
          setGrowthError(null);
        }
      })
      .catch((e) => {
        if (!active) return;
        if (e instanceof ApiForbiddenError) {
          setForbiddenArea(area);
          return;
        }
        if (needsReauth(e)) {
          active = false;
          loginWithRedirect().catch(() => {
            setGrowthError('Authentication required. Please refresh the page.');
            setGrowthLoading(false);
          });
          return;
        }
        setGrowthError(String(e));
      })
      .finally(() => active && setGrowthLoading(false));
    return () => {
      active = false;
    };
  }, [growthGrain, growthFrom, growthTo, area, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (area !== 'Operations & Service') return;
    let active = true;
    setOpsLoading(true);
    setForbiddenArea(null);
    const getToken = () => getAccessTokenSilently();
    fetchOperations(getToken)
      .then((d) => {
        if (active) {
          setOpsData(d);
          setOpsError(null);
        }
      })
      .catch((e) => {
        if (!active) return;
        if (e instanceof ApiForbiddenError) {
          setForbiddenArea(area);
          return;
        }
        if (needsReauth(e)) {
          active = false;
          loginWithRedirect().catch(() => {
            setOpsError('Authentication required. Please refresh the page.');
            setOpsLoading(false);
          });
          return;
        }
        setOpsError(String(e));
      })
      .finally(() => active && setOpsLoading(false));
    return () => {
      active = false;
    };
  }, [area, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (area !== 'Retention & Expansion') return;
    let active = true;
    setRetLoading(true);
    setForbiddenArea(null);
    const getToken = () => getAccessTokenSilently();
    fetchRetention(retGrain, retFrom, retTo, getToken)
      .then((d) => {
        if (active) {
          setRetData(d);
          setRetError(null);
        }
      })
      .catch((e) => {
        if (!active) return;
        if (e instanceof ApiForbiddenError) {
          setForbiddenArea(area);
          return;
        }
        if (needsReauth(e)) {
          active = false;
          loginWithRedirect().catch(() => {
            setRetError('Authentication required. Please refresh the page.');
            setRetLoading(false);
          });
          return;
        }
        setRetError(String(e));
      })
      .finally(() => active && setRetLoading(false));
    return () => {
      active = false;
    };
  }, [retGrain, retFrom, retTo, area, getAccessTokenSilently, loginWithRedirect]);

  if (forbidden) {
    return (
      <Page>
        <div className='p-8 text-sm text-on-surface-secondary'>{FORBIDDEN_MESSAGE}</div>
      </Page>
    );
  }

  if (area === 'Wealth & AUM') {
    if (wealthError) {
      return (
        <Page>
          <div className='p-8 text-sm text-on-surface-secondary'>
            Failed to load wealth data: {wealthError}
          </div>
        </Page>
      );
    }
    return (
      <Page>
        <WealthView
          data={wealthData}
          revenueData={data}
          loading={wealthLoading || !wealthData}
          grain={wealthGrain}
          onGrain={setWealthGrain}
          preset={wealthPreset}
          from={wealthFrom}
          to={wealthTo}
          onPreset={applyWealthPreset}
          onFrom={(v) => {
            setWealthFrom(v);
            setWealthPreset(null);
          }}
          onTo={(v) => {
            setWealthTo(v);
            setWealthPreset(null);
          }}
        />
      </Page>
    );
  }

  if (area === 'Growth & Acquisition') {
    if (growthError) {
      return (
        <Page>
          <div className='p-8 text-sm text-on-surface-secondary'>
            Failed to load growth data: {growthError}
          </div>
        </Page>
      );
    }
    return (
      <Page>
        <GrowthView
          data={growthData}
          loading={growthLoading || !growthData}
          grain={growthGrain}
          onGrain={setGrowthGrain}
          preset={growthPreset}
          from={growthFrom}
          to={growthTo}
          onPreset={applyGrowthPreset}
          onFrom={(v) => {
            setGrowthFrom(v);
            setGrowthPreset(null);
          }}
          onTo={(v) => {
            setGrowthTo(v);
            setGrowthPreset(null);
          }}
        />
      </Page>
    );
  }

  if (area === 'Operations & Service') {
    if (opsError) {
      return (
        <Page>
          <div className='p-8 text-sm text-on-surface-secondary'>
            Failed to load operations data: {opsError}
          </div>
        </Page>
      );
    }
    return (
      <Page>
        <OperationsView data={opsData} loading={opsLoading || !opsData} />
      </Page>
    );
  }

  if (area === 'Retention & Expansion') {
    if (retError) {
      return (
        <Page>
          <div className='p-8 text-sm text-on-surface-secondary'>
            Failed to load retention data: {retError}
          </div>
        </Page>
      );
    }
    return (
      <Page>
        <RetentionView
          data={retData}
          loading={retLoading || !retData}
          grain={retGrain}
          onGrain={setRetGrain}
          preset={retPreset}
          from={retFrom}
          to={retTo}
          onPreset={applyRetPreset}
          onFrom={(v) => {
            setRetFrom(v);
            setRetPreset(null);
          }}
          onTo={(v) => {
            setRetTo(v);
            setRetPreset(null);
          }}
        />
      </Page>
    );
  }

  if (area !== 'Revenue') {
    return (
      <Page>
        <AreaView area={area} />
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className='p-8 text-sm text-on-surface-secondary'>
          Failed to load revenue data: {error}
        </div>
      </Page>
    );
  }

  const loadingData = loading || !data;
  const grainLabel = (GRAINS.find((g) => g.key === grain)?.label ?? 'Month').toLowerCase();
  return (
    <Page>
      <Hero
        loading={loadingData}
        data={data}
        note={
          data && !loadingData
            ? `Stripe & WooCommerce · ${data.meta.windowStart} – ${data.meta.windowEnd} · by ${grainLabel}`
            : `Stripe & WooCommerce · by ${grainLabel}`
        }
      />
      <div className='mb-6 flex flex-wrap items-center justify-between gap-3'>
        <RangeControl
          preset={preset}
          from={from}
          to={to}
          onPreset={applyPreset}
          onFrom={(v) => {
            setFrom(v);
            setPreset(null);
          }}
          onTo={(v) => {
            setTo(v);
            setPreset(null);
          }}
        />
        <GrainSlicer grain={grain} onGrain={setGrain} loading={loadingData} />
      </div>
      <RevenueView data={data} loading={loadingData} />
      <LlcSalesSection data={llcData} loading={llcLoading || !llcData} error={llcError} />
    </Page>
  );
};

// ── primitives ───────────────────────────────────────────────────────────────

const Skeleton: React.FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className = '', style }) => (
  <div className={'animate-pulse rounded bg-surface-tertiary ' + className} style={style} />
);

const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='mx-auto max-w-7xl px-4 pb-20 pt-8 sm:px-6 lg:px-8'>
    {children}
    <footer className='mt-12 border-t border-theme-border pt-6 text-center text-xs text-on-surface-quaternary'>
      <p>© 2026 Digital Ascension Group</p>
    </footer>
  </div>
);

const RangeControl: React.FC<{
  preset: RangeKey | null;
  from: string;
  to: string;
  onPreset: (k: RangeKey) => void;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}> = ({ preset, from, to, onPreset, onFrom, onTo }) => {
  const dateCls =
    'rounded-md border border-theme-border bg-surface px-2 py-1 text-xs text-on-surface [color-scheme:light] dark:[color-scheme:dark]';
  return (
    <div className='flex flex-wrap items-center gap-3'>
      <div className='inline-flex overflow-hidden rounded-lg border border-theme-border'>
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => onPreset(r.key)}
            className={
              'px-3 py-1.5 text-xs font-medium transition-colors ' +
              (preset === r.key
                ? 'bg-surface-tertiary text-on-surface'
                : 'text-on-surface-tertiary hover:text-on-surface')
            }
          >
            {r.label}
          </button>
        ))}
      </div>
      <div className='flex items-center gap-1.5 text-xs text-on-surface-tertiary'>
        <input
          type='date'
          value={from}
          max={to}
          onChange={(e) => onFrom(e.target.value)}
          className={dateCls}
          aria-label='From date'
        />
        <span>–</span>
        <input
          type='date'
          value={to}
          min={from}
          onChange={(e) => onTo(e.target.value)}
          className={dateCls}
          aria-label='To date'
        />
      </div>
    </div>
  );
};

const GrainSlicer: React.FC<{
  grain: Grain;
  onGrain: (g: Grain) => void;
  loading?: boolean;
}> = ({ grain, onGrain, loading }) => (
  <div className='flex items-center gap-2'>
    <span className='text-[11px] uppercase tracking-wide text-on-surface-quaternary'>
      {loading ? 'Updating…' : 'Group by'}
    </span>
    <div className='inline-flex overflow-hidden rounded-lg border border-theme-border'>
      {GRAINS.map((g) => (
        <button
          key={g.key}
          onClick={() => onGrain(g.key)}
          className={
            'px-3 py-1.5 text-xs font-medium transition-colors ' +
            (grain === g.key
              ? 'bg-surface-tertiary text-on-surface'
              : 'text-on-surface-tertiary hover:text-on-surface')
          }
        >
          {g.label}
        </button>
      ))}
    </div>
  </div>
);

// ── hero (static chrome, shadowed numbers) ───────────────────────────────────

const Hero: React.FC<{
  data: RevenuePayload | null;
  loading: boolean;
  note: string;
}> = ({ data, loading, note }) => (
  <section
    className='mb-5 overflow-hidden rounded-2xl border border-theme-border bg-surface p-6 sm:p-8'
    style={{ boxShadow: 'var(--color-card-edge)' }}
  >
    <div className='flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between'>
      <div>
        <div
          className='mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]'
          style={{ color: 'var(--color-primary)' }}
        >
          Revenue
        </div>
        <h1
          className='text-4xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: SERIF }}
        >
          Revenue Overview
        </h1>
        <p className='mt-2 text-sm text-on-surface-secondary'>{note}</p>
      </div>
      <div className='flex shrink-0 divide-x divide-theme-border'>
        <Stat
          label='Revenue'
          loading={loading}
          value={data && usd(data.kpis.resolvedRevenueUsd, true)}
          sub='Gross, before refunds'
        />
        <Stat
          label='Collected'
          loading={loading}
          value={data && usd(data.kpis.grossChargedUsd, true)}
          sub='Gross, before refunds'
        />
        <Stat
          label='Active Subs'
          loading={loading}
          value={data && String(data.kpis.activeSubscriptions)}
        />
      </div>
    </div>
  </section>
);

const Stat: React.FC<{
  value: React.ReactNode;
  label: string;
  sub?: React.ReactNode;
  loading?: boolean;
}> = ({ value, label, sub, loading }) => (
  <div className='px-5 text-center first:pl-0 last:pr-0'>
    {loading ? (
      <Skeleton className='mx-auto h-8 w-20' />
    ) : (
      <div className='text-3xl font-semibold text-on-surface' style={{ fontFamily: SERIF }}>
        {value}
      </div>
    )}
    <div className='mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-on-surface-tertiary'>
      {label}
    </div>
    {!loading && sub && <div className='mt-0.5 text-[10px] text-on-surface-quaternary'>{sub}</div>}
  </div>
);

// ── KPI tile (static label, shadowed value) ──────────────────────────────────

const StatTile: React.FC<{
  id?: string;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  loading?: boolean;
}> = ({ id, label, value, sub, loading }) => (
  <Card id={id} className={id ? 'scroll-mt-20' : ''}>
    <div className='text-[10px] font-semibold uppercase tracking-[0.14em] text-on-surface-tertiary'>
      {label}
    </div>
    {loading ? (
      <>
        <Skeleton className='mt-3 h-7 w-24' />
        <Skeleton className='mt-2.5 h-2.5 w-16' />
      </>
    ) : (
      <>
        <div
          className='mt-2 text-3xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: SERIF }}
        >
          {value}
        </div>
        {sub && <div className='mt-1 text-xs text-on-surface-tertiary'>{sub}</div>}
      </>
    )}
  </Card>
);

// ── net-by-category bar chart (RIA / asset / fund breakdowns share this shape) ──

const NetByCategoryChart: React.FC<{
  id: string;
  title: string;
  source: string;
  caption: string;
  data: { category: string; netUsd: number }[];
  loading: boolean;
}> = ({ id, title, source, caption, data, loading }) => {
  const height = Math.max(140, data.length * 38);
  return (
    <ChartCard
      id={id}
      className='lg:col-span-4'
      title={title}
      source={source}
      height={height}
      loading={loading}
      empty={data.length === 0}
      caption={caption}
    >
      <ResponsiveContainer width='100%' height={height}>
        <BarChart data={data} layout='vertical' margin={{ left: 8, right: 64, top: 4, bottom: 4 }}>
          <XAxis type='number' hide />
          <YAxis
            type='category'
            dataKey='category'
            width={110}
            {...cleanAxis}
            tick={{ fill: AXIS, fontSize: 11 }}
          />
          <Tooltip
            contentStyle={tooltipStyle}
            cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
            formatter={fmtUsd}
          />
          <ReferenceLine x={0} stroke={GRID} />
          <Bar
            dataKey='netUsd'
            name='Net new AUM'
            radius={[0, 5, 5, 0]}
            barSize={15}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.category} fill={d.netUsd < 0 ? RED : BRAND} />
            ))}
            <LabelList
              dataKey='netUsd'
              position='right'
              formatter={fmtUsdCompact}
              style={{
                fill: 'var(--color-on-surface-secondary)',
                fontSize: 11,
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── chart card: static title, shadowed plot ──────────────────────────────────

const ChartCard: React.FC<{
  id?: string;
  className?: string;
  title: string;
  source: string;
  right?: React.ReactNode;
  height: number;
  loading: boolean;
  empty?: boolean;
  caption?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, className, title, source, right, height, loading, empty, caption, children }) => (
  <Card id={id} className={`scroll-mt-20 ${className ?? ''}`}>
    <CardTitle title={title} source={source} right={loading ? undefined : right} />
    {loading ? (
      <Skeleton className='w-full' style={{ height }} />
    ) : empty ? (
      <div
        className='flex items-center justify-center text-sm text-on-surface-tertiary'
        style={{ height }}
      >
        No data available
      </div>
    ) : (
      children
    )}
    {!loading && !empty && caption && (
      <div className='mt-2 text-[11px] leading-relaxed text-on-surface-quaternary'>{caption}</div>
    )}
  </Card>
);

// USD/quantity toggle for charts that carry both a dollar figure and a count
// (token quantity, products purchased) for the same bar. Lives in ChartCard's `right`
// slot so it reads as a header-level display option, distinct from the
// expand/collapse controls in `caption` below the chart.
const UsdQtyToggle: React.FC<{ mode: 'usd' | 'qty'; onChange: (mode: 'usd' | 'qty') => void }> = ({
  mode,
  onChange,
}) => (
  <div className='flex items-center gap-1 text-[11px]'>
    {(['usd', 'qty'] as const).map((m) => (
      <button
        key={m}
        onClick={() => onChange(m)}
        className={
          mode === m
            ? 'font-semibold text-on-surface'
            : 'text-on-surface-tertiary hover:text-on-surface-secondary'
        }
      >
        {m === 'usd' ? 'USD' : 'Qty'}
      </button>
    ))}
  </div>
);

// ── revenue view ─────────────────────────────────────────────────────────────

// kpis.netRevenueUsd, kpis.refundedUsd, dataQuality.refundedUsd, and
// paymentsHealth[].refundedUsd are exposed by the API but have no chart/tile
// here yet — API-first; UI wiring is a follow-up, not an oversight.
const RevenueView: React.FC<{
  data: RevenuePayload | null;
  loading: boolean;
}> = ({ data, loading }) => {
  const k = data?.kpis;
  const totalPaid = data ? data.paymentsHealth.reduce((s, d) => s + d.paid, 0) : 0;
  const totalRefunded = data ? data.paymentsHealth.reduce((s, d) => s + d.refunded, 0) : 0;
  const totalFailed = data ? data.paymentsHealth.reduce((s, d) => s + d.failed, 0) : 0;

  // Null-safe chart inputs: while loading, data is null and ChartCard renders a
  // skeleton instead of these — but JSX children are built eagerly, so they must
  // not dereference null.
  const byBusinessLine = data?.byBusinessLine ?? [];
  const fb = data?.feeBreakdown;
  // AUM fees are a point-in-time snapshot (not windowed) — merging them into the
  // windowed productMix would make share percentages shift incorrectly when the
  // date range changes. Only include onboarding fees, which are windowed.
  const feeEntries = [
    ...(fb?.onboardingFeesUsd
      ? [
          {
            product: 'Onboarding Fees',
            revenueUsd: fb.onboardingFeesUsd,
            share: 0,
          },
        ]
      : []),
  ];
  const mixItems = [...(data?.productMix ?? []), ...feeEntries];
  const mixTotal = mixItems.reduce((s, p) => s + p.revenueUsd, 0);
  const productMix =
    mixTotal > 0 ? mixItems.map((p) => ({ ...p, share: p.revenueUsd / mixTotal })) : mixItems;
  const mrrByBusinessLine = data?.mrrByBusinessLine ?? [];
  const paymentsHealth = data?.paymentsHealth ?? [];
  const revenueTrend = data?.revenueTrend ?? [];

  return (
    <>
      <section className='mb-8 grid grid-cols-2 gap-5 md:grid-cols-4'>
        <StatTile
          id='kpi-rev-per-client'
          loading={loading}
          label='Revenue / Client'
          value={k && usd(k.revenuePerClientUsd)}
          sub={k ? `${k.payingClients} clients · gross, before refunds` : ''}
        />
        <StatTile
          loading={loading}
          label='Revenue Categorized'
          value={k && `${k.resolvedCoveragePct}%`}
          sub='of total collected'
        />
        <StatTile
          loading={loading}
          label='Distinct Clients'
          value={k && String(k.distinctClients)}
          sub='with paid revenue'
        />
        <StatTile
          loading={loading}
          label='Recurring Revenue'
          value={k?.mrrUsd != null ? `${usd(k.mrrUsd, true)}/mo` : '—'}
          sub={k?.arrUsd != null ? `${usd(k.arrUsd, true)} ARR` : 'coming soon'}
        />
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Revenue Detail
      </h2>
      <section className='grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-revenue-line'
          className='lg:col-span-7'
          title='Revenue by Business Line'
          source='Which services bring in revenue'
          height={236}
          loading={loading}
          empty={byBusinessLine.length === 0}
          caption={
            'Gross, before refunds. “Unmapped” products are not yet categorized; “Test Products” are internal test entries.'
          }
        >
          <ResponsiveContainer width='100%' height={236}>
            <BarChart
              data={byBusinessLine}
              layout='vertical'
              margin={{ left: 8, right: 56, top: 4, bottom: 4 }}
            >
              <XAxis type='number' hide />
              <YAxis
                type='category'
                dataKey='businessLine'
                width={104}
                {...cleanAxis}
                tick={{ fill: AXIS, fontSize: 11 }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtUsd}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
              />
              <Bar
                dataKey='revenueUsd'
                fill={BRAND}
                radius={[0, 5, 5, 0]}
                barSize={15}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey='revenueUsd'
                  position='right'
                  formatter={fmtUsdCompact}
                  style={{
                    fill: 'var(--color-on-surface-secondary)',
                    fontSize: 11,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          id='chart-mix'
          className='lg:col-span-5'
          title='Revenue Mix'
          source='Share of revenue by product'
          height={184}
          loading={loading}
          empty={productMix.length === 0}
          caption='Gross, before refunds.'
        >
          <div className='flex items-center gap-4'>
            <div className='relative shrink-0' style={{ width: 150 }}>
              <ResponsiveContainer width='100%' height={184}>
                <PieChart>
                  <Pie
                    data={productMix}
                    dataKey='revenueUsd'
                    nameKey='product'
                    innerRadius={52}
                    outerRadius={74}
                    paddingAngle={2}
                    stroke='none'
                    isAnimationActive={false}
                  >
                    {productMix.map((_, i) => (
                      <Cell key={i} fill={GOLD[i % GOLD.length]} />
                    ))}
                  </Pie>
                  {/* Recharts sets no z-index on its tooltip wrapper, so without one
                        the "total" overlay below — later in DOM order — paints over it. */}
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={fmtUsd}
                    wrapperStyle={{ zIndex: 10 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
                <span className='figure text-base font-semibold text-on-surface'>
                  {usd(mixTotal, true)}
                </span>
                <span className='text-[10px] uppercase tracking-wide text-on-surface-quaternary'>
                  total
                </span>
              </div>
            </div>
            <ul className='min-w-0 flex-1 space-y-2 text-xs text-on-surface-secondary'>
              {productMix.map((p, i) => (
                <li key={p.product} className='flex items-center gap-2'>
                  <span
                    className='h-2 w-2 shrink-0 rounded-sm'
                    style={{ background: GOLD[i % GOLD.length] }}
                  />
                  <span className='min-w-0 flex-1 truncate' title={p.product}>
                    {p.product}
                  </span>
                  <span className='figure shrink-0 tabular-nums text-on-surface-tertiary'>
                    {Math.round(p.share * 100)}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </ChartCard>

        <ChartCard
          id='chart-mrr'
          className='lg:col-span-7'
          title='Recurring Revenue (MRR / ARR)'
          source='Monthly recurring revenue by service line'
          height={170}
          loading={loading}
          empty={mrrByBusinessLine.length === 0}
          right={
            k?.mrrUsd != null ? (
              <div className='text-right'>
                <div className='figure text-lg font-semibold text-on-surface'>
                  {usd(k.mrrUsd, true)}/mo
                </div>
                <div className='text-[11px] text-on-surface-tertiary'>
                  {usd(k.arrUsd ?? 0, true)} ARR
                </div>
              </div>
            ) : undefined
          }
          caption={`Run-rate of the ${k?.activeSubscriptions ?? 0} subscriptions active now — a point-in-time figure, so it doesn't move with the time range above.`}
        >
          <ResponsiveContainer width='100%' height={170}>
            <BarChart
              data={mrrByBusinessLine}
              layout='vertical'
              margin={{ left: 8, right: 64, top: 4, bottom: 4 }}
            >
              <XAxis type='number' hide />
              <YAxis
                type='category'
                dataKey='businessLine'
                width={104}
                {...cleanAxis}
                tick={{ fill: AXIS, fontSize: 11 }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: any, n: any) => [
                  usd(Number(v)) + (n === 'mrrUsd' ? '/mo' : ''),
                  n === 'mrrUsd' ? 'MRR' : 'ARR',
                ]}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
              />
              <Bar
                dataKey='mrrUsd'
                fill={BRAND}
                radius={[0, 5, 5, 0]}
                barSize={15}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey='mrrUsd'
                  position='right'
                  formatter={(v: any) => usd(Number(v), true) + '/mo'}
                  style={{
                    fill: 'var(--color-on-surface-secondary)',
                    fontSize: 11,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          id='chart-payments'
          className='lg:col-span-5'
          title='Payments Health'
          source='Successful, failed & refunded payments'
          height={188}
          loading={loading}
          empty={paymentsHealth.length === 0}
          caption={
            <>
              <div className='flex flex-wrap gap-3 text-xs text-on-surface-secondary'>
                <span className='flex items-center gap-1.5'>
                  <i className='h-2.5 w-2.5 rounded' style={{ background: GREEN }} /> Paid{' '}
                  {totalPaid}
                </span>
                <span className='flex items-center gap-1.5'>
                  <i className='h-2.5 w-2.5 rounded bg-yellow-400' /> Failed {totalFailed}
                </span>
                <span className='flex items-center gap-1.5'>
                  <i className='h-2.5 w-2.5 rounded' style={{ background: RED }} /> Refunded{' '}
                  {totalRefunded}
                </span>
              </div>
              <div className='mt-1'>
                Refunds shown here aren&rsquo;t subtracted from the revenue figures elsewhere on
                this page.
              </div>
            </>
          }
        >
          <ResponsiveContainer width='100%' height={188}>
            <BarChart data={paymentsHealth} margin={{ left: 0, right: 4, top: 6, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='day'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={20}
              />
              <YAxis hide />
              <Tooltip
                contentStyle={tooltipStyle}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
              />
              <Bar
                dataKey='paid'
                stackId='a'
                fill={GREEN}
                radius={[3, 3, 0, 0]}
                maxBarSize={18}
                isAnimationActive={false}
              />
              <Bar
                dataKey='refunded'
                stackId='a'
                fill={RED}
                maxBarSize={18}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          className='lg:col-span-12'
          title='Revenue Trend'
          source='Revenue collected per day'
          height={210}
          loading={loading}
          empty={revenueTrend.length === 0}
          caption='Gross, before refunds — see Payments Health for refund activity.'
        >
          <ResponsiveContainer width='100%' height={210}>
            <AreaChart data={revenueTrend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id='trendGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor='#ba9965' stopOpacity={0.35} />
                  <stop offset='100%' stopColor='#ba9965' stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='day'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={28}
              />
              <YAxis {...cleanAxis} width={40} tick={axisTick} tickFormatter={fmtUsdCompact} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtUsd}
                cursor={{
                  stroke: 'var(--color-border-secondary)',
                  strokeDasharray: '3 3',
                }}
              />
              <Area
                type='monotone'
                dataKey='revenueUsd'
                stroke='#ba9965'
                strokeWidth={2}
                fill='url(#trendGradient)'
                dot={revenueTrend.length <= 8 ? { r: 3, fill: '#ba9965', strokeWidth: 0 } : false}
                activeDot={{ r: 3.5, fill: '#ba9965', strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>
    </>
  );
};

// ── LLC sales ────────────────────────────────────────────────────────────────

const LLC_NEW = '#d1b88d';
const LLC_RENEWAL = '#977b4a';
const LLC_MRR = '#a49b94';

const LlcSalesSection: React.FC<{
  data: LlcSalesPayload | null;
  loading: boolean;
  error: string | null;
}> = ({ data, loading, error }) => {
  const [metric, setMetric] = useState<'usd' | 'qty'>('usd');
  const t = data?.totals;
  const trend = data?.trend ?? [];
  const isUsd = metric === 'usd';
  const products = (n: number) => `${n} ${n === 1 ? 'product' : 'products'}`;

  return (
    <>
      <h2
        className='mb-3 mt-8 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        LLC Sales
      </h2>
      {error ? (
        <Card>
          <div className='text-sm text-on-surface-secondary'>Failed to load LLC sales: {error}</div>
        </Card>
      ) : (
        <>
          <section className='mb-5 grid grid-cols-2 gap-5 md:grid-cols-4'>
            <StatTile
              loading={loading}
              label='New'
              value={t && usd(t.newUsd, true)}
              sub={t && products(t.newCount)}
            />
            <StatTile
              loading={loading}
              label='Renewal'
              value={t && usd(t.renewalUsd, true)}
              sub={t && products(t.renewalCount)}
            />
            <StatTile
              loading={loading}
              label='Subscriptions'
              value={t && usd(t.mrrUsd, true)}
              sub={t && products(t.mrrCount)}
            />
            <StatTile
              loading={loading}
              label='LLC Total'
              value={t && usd(t.newUsd + t.renewalUsd + t.mrrUsd, true)}
              sub={t && products(t.newCount + t.renewalCount + t.mrrCount)}
            />
          </section>
          <section className='grid grid-cols-1 gap-5 lg:grid-cols-12'>
            <ChartCard
              id='chart-llc-sales'
              className='lg:col-span-12'
              title='LLC Sales: New, Renewal and Subscriptions'
              source={isUsd ? 'LLC revenue per period' : 'LLC products purchased per period'}
              height={210}
              loading={loading}
              empty={trend.length === 0}
              right={<UsdQtyToggle mode={metric} onChange={setMetric} />}
              caption={
                <>
                  <div className='flex flex-wrap gap-3 text-xs text-on-surface-secondary'>
                    <span className='flex items-center gap-1.5'>
                      <i className='h-2.5 w-2.5 rounded' style={{ background: LLC_NEW }} /> New
                    </span>
                    <span className='flex items-center gap-1.5'>
                      <i className='h-2.5 w-2.5 rounded' style={{ background: LLC_RENEWAL }} />{' '}
                      Renewal
                    </span>
                    <span className='flex items-center gap-1.5'>
                      <i className='h-2.5 w-2.5 rounded' style={{ background: LLC_MRR }} />{' '}
                      Subscriptions
                    </span>
                  </div>
                  <div className='mt-1'>
                    Gross, before refunds. Subscriptions is every subscription payment billed in the
                    period, first month and annual plans in full; it is not MRR. Renewal is any
                    other purchase for an LLC brought in through the DAG App import feature. New is
                    everything else, including all WooCommerce history.
                  </div>
                </>
              }
            >
              <ResponsiveContainer width='100%' height={210}>
                <BarChart data={trend} margin={{ left: 0, right: 4, top: 6, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis
                    dataKey='bucket'
                    {...cleanAxis}
                    tick={axisTick}
                    interval='preserveStartEnd'
                    minTickGap={20}
                  />
                  <YAxis
                    {...cleanAxis}
                    width={40}
                    tick={axisTick}
                    tickFormatter={isUsd ? fmtUsdCompact : fmtCount}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={isUsd ? fmtUsd : fmtCount}
                    cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
                  />
                  <Bar
                    dataKey={isUsd ? 'newUsd' : 'newCount'}
                    name='New'
                    stackId='llc'
                    fill={LLC_NEW}
                    maxBarSize={18}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey={isUsd ? 'renewalUsd' : 'renewalCount'}
                    name='Renewal'
                    stackId='llc'
                    fill={LLC_RENEWAL}
                    maxBarSize={18}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey={isUsd ? 'mrrUsd' : 'mrrCount'}
                    name='Subscriptions'
                    stackId='llc'
                    fill={LLC_MRR}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={18}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>
        </>
      )}
    </>
  );
};

// ── growth view ──────────────────────────────────────────────────────────────

const fmtCount = (v: any) => String(Math.round(Number(v)));

const GrowthView: React.FC<{
  data: GrowthPayload | null;
  loading: boolean;
  grain: Grain;
  onGrain: (g: Grain) => void;
  preset: RangeKey | null;
  from: string;
  to: string;
  onPreset: (k: RangeKey) => void;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}> = ({ data, loading, grain, onGrain, preset, from, to, onPreset, onFrom, onTo }) => {
  const clientGrowth = data?.clientGrowth ?? [];
  const latest = clientGrowth[clientGrowth.length - 1];
  const grainLabel = (GRAINS.find((g) => g.key === grain)?.label ?? 'Month').toLowerCase();
  return (
    <>
      <header className='mb-5'>
        <div
          className='mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]'
          style={{ color: 'var(--color-primary)' }}
        >
          Growth & Acquisition
        </div>
        <h1
          className='text-4xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: SERIF }}
        >
          Client Growth
        </h1>
        <p className='mt-2 text-sm text-on-surface-secondary'>
          DAG App · DWP Onboarding · cumulative by {grainLabel}
        </p>
      </header>

      <div className='mb-6 flex flex-wrap items-center justify-between gap-3'>
        <RangeControl
          preset={preset}
          from={from}
          to={to}
          onPreset={onPreset}
          onFrom={onFrom}
          onTo={onTo}
        />
        <GrainSlicer grain={grain} onGrain={onGrain} loading={loading} />
      </div>

      <section id='stat-client-count' className='mb-8 grid grid-cols-2 gap-5 md:grid-cols-3'>
        <StatTile
          loading={loading}
          label='Private Clients'
          value={latest && String(latest.privateClients)}
          sub='total to date'
        />
        <StatTile
          loading={loading}
          label='Wealth Clients'
          value={latest && String(latest.wealthClients)}
          sub='total to date'
        />
        <StatTile
          loading={loading}
          label='In Both'
          value={latest && String(latest.both)}
          sub='distinct emails in both'
        />
      </section>

      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-client-growth'
          className='lg:col-span-12'
          title='Clients Over Time'
          source='Cumulative clients by product line'
          height={320}
          loading={loading}
          empty={clientGrowth.length === 0}
          caption='Cumulative counts as of the end of each period. “Both” counts distinct emails that appear in both Private Clients and Wealth.'
        >
          <ResponsiveContainer width='100%' height={320}>
            <LineChart data={clientGrowth} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='bucket'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={28}
              />
              <YAxis {...cleanAxis} width={40} tick={axisTick} allowDecimals={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtCount}
                cursor={{
                  stroke: 'var(--color-border-secondary)',
                  strokeDasharray: '3 3',
                }}
              />
              <Legend
                verticalAlign='top'
                align='right'
                height={28}
                iconType='plainline'
                iconSize={14}
                wrapperStyle={{
                  fontSize: 11,
                  color: 'var(--color-on-surface-secondary)',
                }}
              />
              <Line
                type='monotone'
                dataKey='privateClients'
                name='Private Clients'
                stroke={BRAND}
                strokeWidth={2}
                dot={clientGrowth.length <= 12 ? { r: 3, fill: BRAND, strokeWidth: 0 } : false}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <Line
                type='monotone'
                dataKey='wealthClients'
                name='Wealth'
                stroke={GREEN}
                strokeWidth={2}
                dot={clientGrowth.length <= 12 ? { r: 3, fill: GREEN, strokeWidth: 0 } : false}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <Line
                type='monotone'
                dataKey='both'
                name='Both'
                stroke={GOLD[5]}
                strokeWidth={2}
                strokeDasharray='5 4'
                dot={clientGrowth.length <= 12 ? { r: 3, fill: GOLD[5], strokeWidth: 0 } : false}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        All Growth & Acquisition KPIs
      </h2>
      <KpiChipGrid area='Growth & Acquisition' />
    </>
  );
};

// ── retention view ───────────────────────────────────────────────────────────

// Rates are null when a period has no starting book; null must read as a dash,
// never "0%".
const fmtPct = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(1)}%`);
const fmtPctTooltip = (v: any) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

const RetentionView: React.FC<{
  data: RetentionPayload | null;
  loading: boolean;
  grain: Grain;
  onGrain: (g: Grain) => void;
  preset: RangeKey | null;
  from: string;
  to: string;
  onPreset: (k: RangeKey) => void;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}> = ({ data, loading, grain, onGrain, preset, from, to, onPreset, onFrom, onTo }) => {
  const rev = data?.revenue;
  const assets = data?.assets;
  const revTrend = rev?.trend ?? [];
  const assetTrend = assets?.trend ?? [];
  const byLine = rev?.byBusinessLine ?? [];
  const grainLabel = (GRAINS.find((g) => g.key === grain)?.label ?? 'Month').toLowerCase();
  const dots = (n: number, color: string) =>
    n <= 12 ? { r: 3, fill: color, strokeWidth: 0 } : false;
  const legendProps = {
    verticalAlign: 'top' as const,
    align: 'right' as const,
    height: 28,
    iconType: 'plainline' as const,
    iconSize: 14,
    wrapperStyle: { fontSize: 11, color: 'var(--color-on-surface-secondary)' },
  };
  const cursor = { stroke: 'var(--color-border-secondary)', strokeDasharray: '3 3' };

  return (
    <>
      <header className='mb-5'>
        <div
          className='mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]'
          style={{ color: 'var(--color-primary)' }}
        >
          Retention & Expansion
        </div>
        <h1
          className='text-4xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: SERIF }}
        >
          Retention
        </h1>
        <p className='mt-2 text-sm text-on-surface-secondary'>
          {data && !loading && data.meta.windowStart
            ? `Stripe subscriptions · Anchorage AUM · ${data.meta.windowStart} – ${data.meta.windowEnd} · by ${grainLabel}`
            : `Stripe subscriptions · Anchorage AUM · by ${grainLabel}`}
        </p>
      </header>

      <div className='mb-6 flex flex-wrap items-center justify-between gap-3'>
        <RangeControl
          preset={preset}
          from={from}
          to={to}
          onPreset={onPreset}
          onFrom={onFrom}
          onTo={onTo}
        />
        <GrainSlicer grain={grain} onGrain={onGrain} loading={loading} />
      </div>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Revenue Retention
      </h2>
      <section
        id='stat-revenue-retention'
        className='mb-8 grid scroll-mt-20 grid-cols-2 gap-5 md:grid-cols-4'
      >
        <StatTile
          loading={loading}
          label='Gross Revenue Retention'
          value={fmtPct(rev?.kpis.grossRetentionPct)}
          sub='of starting MRR kept'
        />
        <StatTile
          loading={loading}
          label='Revenue Churn'
          value={fmtPct(rev?.kpis.churnPct)}
          sub={
            rev
              ? `${usd(rev.kpis.churnedMrrUsd, true)} of ${usd(rev.kpis.startingMrrUsd, true)} MRR`
              : ''
          }
        />
        <StatTile
          loading={loading}
          label='Logo Churn'
          value={fmtPct(rev?.kpis.logoChurnPct)}
          sub={
            rev
              ? `${rev.kpis.churnedSubscriptions} of ${rev.kpis.startingSubscriptions} subscriptions`
              : ''
          }
        />
        <StatTile
          loading={loading}
          label='Starting MRR'
          value={rev ? usd(rev.kpis.startingMrrUsd, true) : '—'}
          sub='at the start of the window'
        />
      </section>

      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-revenue-retention'
          className='lg:col-span-7'
          title='Revenue Retention Over Time'
          source='Gross retention and churn of subscription MRR'
          height={260}
          loading={loading}
          empty={revTrend.length === 0}
          caption='Periods with no starting subscriptions have no rate and show as gaps.'
        >
          <ResponsiveContainer width='100%' height={260}>
            <LineChart data={revTrend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='bucket'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={28}
              />
              <YAxis
                {...cleanAxis}
                width={40}
                tick={axisTick}
                tickFormatter={(v) => `${v}%`}
                domain={[0, 100]}
              />
              <Tooltip contentStyle={tooltipStyle} formatter={fmtPctTooltip} cursor={cursor} />
              <Legend {...legendProps} />
              <Line
                type='monotone'
                dataKey='grossRetentionPct'
                name='Gross Retention'
                stroke={GREEN}
                strokeWidth={2}
                dot={dots(revTrend.length, GREEN)}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <Line
                type='monotone'
                dataKey='churnPct'
                name='Revenue Churn'
                stroke={RED}
                strokeWidth={2}
                dot={dots(revTrend.length, RED)}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <Line
                type='monotone'
                dataKey='logoChurnPct'
                name='Logo Churn'
                stroke={GOLD[5]}
                strokeWidth={2}
                strokeDasharray='5 4'
                dot={dots(revTrend.length, GOLD[5])}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          id='chart-retention-by-line'
          className='lg:col-span-5'
          title='Retention by Business Line'
          source='Gross revenue retention over the window'
          height={Math.max(140, byLine.length * 38)}
          loading={loading}
          empty={byLine.length === 0}
          caption='Lines with no starting subscriptions have no rate.'
        >
          <ResponsiveContainer width='100%' height={Math.max(140, byLine.length * 38)}>
            <BarChart
              data={byLine}
              layout='vertical'
              margin={{ left: 8, right: 48, top: 4, bottom: 4 }}
            >
              <XAxis type='number' hide domain={[0, 100]} />
              <YAxis
                type='category'
                dataKey='businessLine'
                width={104}
                {...cleanAxis}
                tick={{ fill: AXIS, fontSize: 11 }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtPctTooltip}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
              />
              <Bar
                dataKey='grossRetentionPct'
                name='Gross Retention'
                fill={BRAND}
                radius={[0, 5, 5, 0]}
                barSize={15}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey='grossRetentionPct'
                  position='right'
                  formatter={fmtPctTooltip}
                  style={{ fill: 'var(--color-on-surface-secondary)', fontSize: 11 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Asset Retention
      </h2>
      <section
        id='stat-asset-retention'
        className='mb-8 grid scroll-mt-20 grid-cols-2 gap-5 md:grid-cols-4'
      >
        <StatTile
          loading={loading}
          label='Gross Asset Retention'
          value={fmtPct(assets?.kpis.grossRetentionPct)}
          sub='1 − outflows / starting AUM'
        />
        <StatTile
          loading={loading}
          label='Net Asset Retention'
          value={fmtPct(assets?.kpis.netRetentionPct)}
          sub='credits deposits too'
        />
        <StatTile
          loading={loading}
          label='Starting AUM'
          value={fmtUsdOrDash(assets?.kpis.startingAumUsd)}
          sub='earliest snapshot at window start'
        />
        <StatTile
          loading={loading}
          label='Net Flows'
          value={assets ? usd(assets.kpis.inflowsUsd - assets.kpis.outflowsUsd, true) : '—'}
          sub={
            assets
              ? `${usd(assets.kpis.inflowsUsd, true)} in · ${usd(assets.kpis.outflowsUsd, true)} out`
              : ''
          }
        />
      </section>

      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-asset-retention'
          className='lg:col-span-12'
          title='Asset Retention Over Time'
          source='Flow-based retention of custodied AUM'
          height={260}
          loading={loading}
          empty={assetTrend.length === 0}
          caption='Flows only — market movement is excluded. Gross is clamped to 0–100%; net can exceed 100%. Periods with no earlier AUM snapshot have no rate.'
        >
          <ResponsiveContainer width='100%' height={260}>
            <LineChart data={assetTrend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='bucket'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={28}
              />
              <YAxis {...cleanAxis} width={40} tick={axisTick} tickFormatter={(v) => `${v}%`} />
              <Tooltip contentStyle={tooltipStyle} formatter={fmtPctTooltip} cursor={cursor} />
              <Legend {...legendProps} />
              <Line
                type='monotone'
                dataKey='grossRetentionPct'
                name='Gross Retention'
                stroke={BRAND}
                strokeWidth={2}
                dot={dots(assetTrend.length, BRAND)}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <Line
                type='monotone'
                dataKey='netRetentionPct'
                name='Net Retention'
                stroke={GREEN}
                strokeWidth={2}
                dot={dots(assetTrend.length, GREEN)}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        All Retention & Expansion KPIs
      </h2>
      <KpiChipGrid area='Retention & Expansion' />
    </>
  );
};

// ── operations view ──────────────────────────────────────────────────────────

// Ages arrive in fractional days. Under a day reads better in hours ("6 hrs"),
// and null (no open tickets) is a dash, never "0 days".
const fmtAge = (days: number | null | undefined) => {
  if (days == null) return '—';
  if (days < 1) {
    const hours = Math.round(days * 24);
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }
  const rounded = Math.round(days * 10) / 10;
  return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
};

const OperationsView: React.FC<{
  data: OperationsPayload | null;
  loading: boolean;
}> = ({ data, loading }) => {
  const t = data?.ticketAge;
  const r = data?.ticketSubmitterRevenue;
  const c = data?.ticketCounts;
  return (
    <>
      <header className='mb-5'>
        <div
          className='mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]'
          style={{ color: 'var(--color-primary)' }}
        >
          Operations & Service
        </div>
        <h1
          className='text-4xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: SERIF }}
        >
          Ticket Age
        </h1>
        <p className='mt-2 text-sm text-on-surface-secondary'>
          Front · open tickets as of {data?.meta.queriedAt ?? 'today'}
          {t ? ` · ${t.openTickets} open` : ''}
        </p>
      </header>

      <section
        id='stat-ticket-age'
        className='mb-8 grid scroll-mt-20 grid-cols-1 gap-5 md:grid-cols-3'
      >
        <StatTile
          loading={loading}
          label='Oldest Ticket'
          value={fmtAge(t?.oldestDays)}
          sub='longest-open ticket'
        />
        <StatTile
          loading={loading}
          label='90th Percentile'
          value={fmtAge(t?.p90Days)}
          sub='older than 90% of open tickets'
        />
        <StatTile
          loading={loading}
          label='Median'
          value={fmtAge(t?.medianDays)}
          sub='older than half of open tickets'
        />
      </section>
      <p className='mb-10 text-[11px] leading-relaxed text-on-surface-quaternary'>
        Age runs from when each ticket was created to now, across tickets that are still open.
        Archived and deleted conversations are not counted.
      </p>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Ticket Counts
      </h2>
      <section
        id='stat-ticket-counts'
        className='mb-8 grid scroll-mt-20 grid-cols-1 gap-5 md:grid-cols-3'
      >
        <StatTile
          loading={loading}
          label='Last 7 Days'
          value={c ? c.last7Days.toLocaleString() : '—'}
          sub='tickets created'
        />
        <StatTile
          loading={loading}
          label='Last 30 Days'
          value={c ? c.last30Days.toLocaleString() : '—'}
          sub='tickets created'
        />
        <StatTile
          loading={loading}
          label='Last 90 Days'
          value={c ? c.last90Days.toLocaleString() : '—'}
          sub='tickets created'
        />
      </section>
      <p className='mb-10 text-[11px] leading-relaxed text-on-surface-quaternary'>
        Rolling windows, counted by creation date regardless of current status. Each window includes
        the shorter ones.
      </p>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Average Ticket Submitter Revenue
      </h2>
      <section
        id='stat-ticket-submitter-revenue'
        className='mb-8 grid scroll-mt-20 grid-cols-1 gap-5 md:grid-cols-2'
      >
        <StatTile
          loading={loading}
          label='Avg Revenue per Ticket Submitter'
          value={fmtUsdOrDash(r?.avgRevenueUsd)}
          sub={`lifetime revenue${
            r
              ? ` · ${r.ticketSubmitters} ${r.ticketSubmitters === 1 ? 'submitter' : 'submitters'} in last 30 days`
              : ''
          }`}
        />
      </section>
      <p className='mb-10 text-[11px] leading-relaxed text-on-surface-quaternary'>
        Average lifetime revenue of clients who have submitted a ticket in the past 30 days. Rolling
        window.
      </p>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        All Operations & Service KPIs
      </h2>
      <KpiChipGrid area='Operations & Service' />
    </>
  );
};

// ── wealth view ──────────────────────────────────────────────────────────────

// Shared by the Funded Accounts and RIAs Onboarded panels — both plot a single
// per-period count bar with the same axes and styling.
const CountTrendChart: React.FC<{
  data: { bucket: string; count: number }[];
}> = ({ data }) => (
  <ResponsiveContainer width='100%' height={210}>
    <BarChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid {...gridProps} />
      <XAxis
        dataKey='bucket'
        {...cleanAxis}
        tick={axisTick}
        interval='preserveStartEnd'
        minTickGap={28}
      />
      <YAxis {...cleanAxis} width={30} tick={axisTick} allowDecimals={false} />
      <Tooltip
        contentStyle={tooltipStyle}
        formatter={fmtCount}
        cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
      />
      <Bar
        dataKey='count'
        fill={BRAND}
        radius={[3, 3, 0, 0]}
        maxBarSize={40}
        isAnimationActive={false}
      >
        <LabelList
          dataKey='count'
          position='top'
          style={{ fill: 'var(--color-on-surface-secondary)', fontSize: 11 }}
        />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const WealthView: React.FC<{
  data: WealthPayload | null;
  revenueData: RevenuePayload | null;
  loading: boolean;
  grain: Grain;
  onGrain: (g: Grain) => void;
  preset: RangeKey | null;
  from: string;
  to: string;
  onPreset: (k: RangeKey) => void;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}> = ({ data, revenueData, loading, grain, onGrain, preset, from, to, onPreset, onFrom, onTo }) => {
  const meetingsTrend = data?.meetingsTrend ?? [];
  const meetingsByAdvisor = data?.meetingsByAdvisor ?? [];
  const fundedAccountsTrend = data?.fundedAccountsTrend ?? [];
  const riaOnboardedTrend = data?.riaOnboardedTrend ?? [];
  const byDate = data?.byDate ?? [];
  const byToken = data?.byToken ?? [];
  const aumTrend = data?.aumTrend ?? [];
  const aumByCustodian = data?.aumByCustodian ?? [];
  const aumByAsset = data?.aumByAsset ?? [];
  const byAdvisorHeight = Math.max(120, meetingsByAdvisor.length * 36);
  const byTokenHeight = Math.max(140, byToken.length * 36);
  const COLLAPSED_TOKEN_ROWS = 5;
  const tokenCollapsedHeight = Math.max(140, COLLAPSED_TOKEN_ROWS * 36);
  const [tokenChartExpanded, setTokenChartExpanded] = useState(false);
  const showTokenToggle = byToken.length > COLLAPSED_TOKEN_ROWS;
  const [tokenMetric, setTokenMetric] = useState<'usd' | 'qty'>('usd');
  const aumCustodiedRows = aumByCustodian.filter((r) => r.custodyScope !== 'heldaway_external');
  const aumExternalRows = aumByCustodian.filter((r) => r.custodyScope === 'heldaway_external');
  const aumCustodiedSubtotal = aumCustodiedRows.reduce((s, r) => s + r.aumUsd, 0);
  const aumGrandTotal = aumByCustodian.reduce((s, r) => s + r.aumUsd, 0);
  // +2 for the always-present header row and grand-total row, +1 more when the
  // custodied-vs-external split adds a subtotal row on top of the grand total.
  const aumByCustodianRows = aumByCustodian.length + 2 + (aumExternalRows.length > 0 ? 1 : 0);
  const aumByCustodianHeight = Math.max(140, aumByCustodianRows * 32);
  const aumByAssetHeight = Math.max(140, aumByAsset.length * 36);
  const COLLAPSED_AUM_ASSET_ROWS = 5;
  const aumAssetCollapsedHeight = Math.max(140, COLLAPSED_AUM_ASSET_ROWS * 36);
  const [aumAssetChartExpanded, setAumAssetChartExpanded] = useState(false);
  const showAumAssetToggle = aumByAsset.length > COLLAPSED_AUM_ASSET_ROWS;
  const [aumAssetMetric, setAumAssetMetric] = useState<'usd' | 'qty'>('usd');
  const latestAum = [...aumTrend].reverse().find((r) => r.totalAumUsd != null);
  const totalNewFunded = fundedAccountsTrend.reduce((s, r) => s + r.count, 0);
  const grainLabel = (GRAINS.find((g) => g.key === grain)?.label ?? 'Month').toLowerCase();
  // Latest bucket that actually has a value snapshot — not simply the last bucket,
  // which may only carry net-funded history. Falls back to the last net-funded
  // bucket so the tile shows something honest rather than an em dash.
  const latestValued = [...byDate].reverse().find((r) => r.avgMarketValueUsd != null);
  const latestFunded = [...byDate].reverse().find((r) => r.avgNetFundedUsd != null);

  // Money movement. Outflows arrive as positive magnitudes and are
  // negated here so the charts read as two-sided flow: money in above the axis,
  // money out below.
  const fk = data?.flowKpis;
  const flowTrend = (data?.flowTrend ?? []).map((d) => ({
    ...d,
    outflowsPlotted: -d.outflowsUsd,
  }));
  const flowsByToken = (data?.flowsByToken ?? []).map((t) => ({
    ...t,
    outflowsPlotted: -t.outflowsUsd,
  }));
  const flowsByAdvisor = data?.flowsByAdvisor ?? [];
  const flowsByFund = data?.flowsByFund ?? [];
  const flowsByTokenHeight = Math.max(140, flowsByToken.length * 38);
  const flowsByAdvisorHeight = Math.max(140, flowsByAdvisor.length * 38);

  // Net New AUM breakdowns (DAG-1108): the same net-flow figures already
  // charted above, per RIA/asset, plus fund — reframed under Net New AUM's
  // own name since NetNewAumUsd is defined as this same signed sum (see
  // FlowKpis.NetNewAumUsd on the backend). Only the net figure is plotted
  // here, not the two-sided in/out view, since Net New AUM is one signed
  // number per the ticket.
  const netNewAumByRia = flowsByAdvisor.map((a) => ({
    category: a.advisorName,
    netUsd: a.netUsd,
  }));
  const netNewAumByAsset = (data?.flowsByToken ?? []).map((t) => ({
    category: t.token,
    netUsd: t.netUsd,
  }));
  const netNewAumByFund = flowsByFund.map((f) => ({
    category: f.fund,
    netUsd: f.netUsd,
  }));

  // The withdrawal action value is confirmed (see docs/data-sources/anchorage.md,
  // Known Gap #1) — this just flags a window with no withdrawal transactions,
  // where net flow equals gross money in.
  const noOutflows = !!fk && fk.inflowTransactions > 0 && fk.outflowTransactions === 0;

  return (
    <>
      <header className='mb-5'>
        <div
          className='mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]'
          style={{ color: 'var(--color-primary)' }}
        >
          Wealth & AUM
        </div>
        <h1
          className='text-4xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: SERIF }}
        >
          Money Movement, Meetings & Account Size
        </h1>
        <p className='mt-2 text-sm text-on-surface-secondary'>
          {data && !loading && data.meta.windowStart
            ? `Salesforce · Anchorage · ${data.meta.windowStart} – ${data.meta.windowEnd} · by ${grainLabel}`
            : `Salesforce · Anchorage · by ${grainLabel}`}
        </p>
      </header>
      <div className='mb-6 flex flex-wrap items-center justify-between gap-3'>
        <RangeControl
          preset={preset}
          from={from}
          to={to}
          onPreset={onPreset}
          onFrom={onFrom}
          onTo={onTo}
        />
        <GrainSlicer grain={grain} onGrain={onGrain} loading={loading} />
      </div>

      {/* Six tiles, not four: the money-movement trio, Net New AUM, plus the
          two account headline figures. grid-cols-6 at lg keeps the row even. */}
      <section className='mb-8 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-6'>
        <StatTile
          loading={loading}
          label='Money In'
          value={fk && usd(fk.grossInflowsUsd, true)}
          sub={fk ? `${fk.inflowTransactions} deposits` : ''}
        />
        <StatTile
          loading={loading}
          label='Money Out'
          value={fk && usd(fk.grossOutflowsUsd, true)}
          sub={fk ? `${fk.outflowTransactions} withdrawals` : ''}
        />
        <StatTile
          loading={loading}
          label='Net Flow'
          value={fk && usd(fk.netFlowUsd, true)}
          sub='new money, excluding market movement'
        />
        {/* Same figure as Net Flow, under its own name — see FlowKpis.NetNewAumUsd
            on the backend. Shown as a distinct tile since Net New AUM is the
            business-facing KPI name, per schema/canon_analytics_wealth.sql.
            sub carries the prior-period comparison (DAG-1108's "month over
            month or between two dates" ask) rather than repeating the
            "excludes market movement" disclaimer already on the Net Flow
            tile right next to it. */}
        <StatTile
          loading={loading}
          label='Net New AUM'
          value={fk && usd(fk.netNewAumUsd, true)}
          sub={
            fk &&
            (fk.netNewAumUsdDeltaPct == null ? (
              'no prior-period data'
            ) : (
              <span style={{ color: fk.netNewAumUsdDeltaPct >= 0 ? GREEN : RED }}>
                {fk.netNewAumUsdDeltaPct >= 0 ? '▲' : '▼'}{' '}
                {Math.abs(fk.netNewAumUsdDeltaPct).toFixed(1)}% vs. prior period
              </span>
            ))
          }
        />
        <StatTile
          loading={loading}
          label='New Funded Accounts'
          value={String(totalNewFunded)}
          sub='first deposit in range'
        />
        {/* Prefer real account value. When no custody snapshot has landed in range
            yet, fall back to net funded — but say so, rather than letting a cost-basis
            figure sit under an "account size" label unqualified. */}
        <StatTile
          loading={loading}
          label='Average Account Size'
          value={
            latestValued
              ? fmtUsdCompact(latestValued.avgMarketValueUsd)
              : latestFunded
                ? fmtUsdCompact(latestFunded.avgNetFundedUsd)
                : '—'
          }
          sub={
            latestValued
              ? `${latestValued.accountCount} live accounts`
              : latestFunded
                ? 'net funded · no value snapshot yet'
                : 'no snapshots in range'
          }
        />
      </section>

      <section className='mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
        <FeeBreakdownCard data={revenueData} loading={revenueData == null} />
      </section>

      {noOutflows && !loading && (
        <div
          className='mb-6 rounded-xl border border-theme-border bg-surface p-4 text-[13px] text-on-surface-secondary'
          style={{ boxShadow: 'var(--color-card-edge)' }}
        >
          <strong>No withdrawals recorded in this period</strong> — net flow equals gross money in
          for this window. If withdrawals were expected, verify the ingestion pipeline before
          relying on this figure.
        </div>
      )}

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Money In &amp; Out
      </h2>
      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-flows-trend'
          className='lg:col-span-12'
          title='Money In vs. Money Out'
          source='Client deposits and withdrawals per period'
          height={230}
          loading={loading}
          empty={flowTrend.length === 0}
          caption='Bars above the line are money arriving; bars below are money leaving.'
        >
          <ResponsiveContainer width='100%' height={230}>
            <BarChart
              data={flowTrend}
              stackOffset='sign'
              margin={{ left: 0, right: 8, top: 6, bottom: 0 }}
            >
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='bucket'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={20}
              />
              <YAxis {...cleanAxis} width={52} tick={axisTick} tickFormatter={fmtUsdAbsCompact} />
              <Tooltip
                contentStyle={tooltipStyle}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
                formatter={fmtUsdAbs}
              />
              <Legend
                verticalAlign='top'
                align='right'
                height={28}
                iconType='square'
                iconSize={9}
                wrapperStyle={{
                  fontSize: 11,
                  color: 'var(--color-on-surface-secondary)',
                }}
              />
              <ReferenceLine y={0} stroke={GRID} />
              {/* Shared stackId + stackOffset="sign" on the chart is what makes this
                  diverge from zero: one column per period, money in above the axis
                  and money out below. Without the stackId the two bars render side
                  by side and the offset does nothing.

                  radius on both bars is [3, 3, 0, 0] — NOT mirrored — despite one
                  bar rendering above zero and the other below. In recharts@3.8.1,
                  Bar computes rect y as the value's own scale position and y+height
                  as the zero-baseline position (Bar.js renderRectangle), so radius
                  index [0,1] always rounds the corner at the value tip (the end
                  farthest from zero) and [2,3] always rounds the corner at the
                  zero baseline, regardless of whether the value is positive or
                  negative — the sign only flips height, not which index pair maps
                  to which edge. So both bars need the same array to round their
                  outer tips; giving "Money out" a mirrored radius (e.g. [0,0,3,3])
                  rounds its zero-adjacent edge instead and looks wrong. Verify
                  against Rectangle.js's getRectanglePath if recharts is upgraded,
                  since this behavior is undocumented and could change. */}
              <Bar
                dataKey='inflowsUsd'
                name='Money in'
                stackId='flow'
                fill={GREEN}
                radius={[3, 3, 0, 0]}
                maxBarSize={26}
                isAnimationActive={false}
              />
              <Bar
                dataKey='outflowsPlotted'
                name='Money out'
                stackId='flow'
                fill={RED}
                radius={[3, 3, 0, 0]}
                maxBarSize={26}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          id='chart-flows-by-token'
          className='lg:col-span-7'
          title='Money Movement by Asset'
          source='Which assets clients move in and out'
          height={flowsByTokenHeight}
          loading={loading}
          empty={flowsByToken.length === 0}
          caption='Ranked by total two-way volume, so an asset with large offsetting moves still ranks.'
        >
          <ResponsiveContainer width='100%' height={flowsByTokenHeight}>
            <BarChart
              data={flowsByToken}
              stackOffset='sign'
              layout='vertical'
              margin={{ left: 8, right: 56, top: 4, bottom: 4 }}
            >
              <XAxis type='number' hide />
              <YAxis
                type='category'
                dataKey='token'
                width={84}
                {...cleanAxis}
                tick={{ fill: AXIS, fontSize: 11 }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
                formatter={fmtUsdAbs}
              />
              <Legend
                verticalAlign='top'
                align='right'
                height={28}
                iconType='square'
                iconSize={9}
                wrapperStyle={{
                  fontSize: 11,
                  color: 'var(--color-on-surface-secondary)',
                }}
              />
              <ReferenceLine x={0} stroke={GRID} />
              {/* Same shared stackId as the trend chart: one bar per asset, money
                  in to the right of zero and money out to the left.

                  radius on both bars is [0, 5, 5, 0] for the same reason as the
                  trend chart above: in recharts@3.8.1, horizontal Bar rects anchor
                  x at the zero baseline and x+width at the value tip (Bar.js
                  renderRectangle), so index [1,2] always rounds the value-tip
                  corner and [0,3] always rounds the zero-baseline corner, whether
                  the value is positive or negative. Both bars need the same array
                  to round their outer tips — do not mirror "Money out"'s radius. */}
              <Bar
                dataKey='inflowsUsd'
                name='Money in'
                stackId='flow'
                fill={GREEN}
                radius={[0, 5, 5, 0]}
                barSize={15}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey='inflowsUsd'
                  position='right'
                  formatter={fmtUsdAbsCompact}
                  style={{
                    fill: 'var(--color-on-surface-secondary)',
                    fontSize: 11,
                  }}
                />
              </Bar>
              <Bar
                dataKey='outflowsPlotted'
                name='Money out'
                stackId='flow'
                fill={RED}
                radius={[0, 5, 5, 0]}
                barSize={15}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          id='chart-flows-by-advisor'
          className='lg:col-span-5'
          title='Net Flow by Advisor'
          source='Money in minus money out, per advisor'
          height={flowsByAdvisorHeight}
          loading={loading}
          empty={flowsByAdvisor.length === 0}
          caption='Advisors without an assigned code are grouped as “(unattributed).”'
        >
          <ResponsiveContainer width='100%' height={flowsByAdvisorHeight}>
            <BarChart
              data={flowsByAdvisor}
              layout='vertical'
              margin={{ left: 8, right: 64, top: 4, bottom: 4 }}
            >
              <XAxis type='number' hide />
              <YAxis
                type='category'
                dataKey='advisorName'
                width={130}
                {...cleanAxis}
                tick={{ fill: AXIS, fontSize: 11 }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
                formatter={fmtUsd}
              />
              <ReferenceLine x={0} stroke={GRID} />
              <Bar
                dataKey='netUsd'
                name='Net flow'
                radius={[0, 5, 5, 0]}
                barSize={15}
                isAnimationActive={false}
              >
                {flowsByAdvisor.map((a) => (
                  <Cell key={a.repCode} fill={a.netUsd < 0 ? RED : BRAND} />
                ))}
                <LabelList
                  dataKey='netUsd'
                  position='right'
                  formatter={fmtUsdCompact}
                  style={{
                    fill: 'var(--color-on-surface-secondary)',
                    fontSize: 11,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Net New AUM Breakdown
      </h2>
      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <NetByCategoryChart
          id='chart-net-new-aum-by-ria'
          title='Net New AUM by RIA'
          source='Net transfers per advisor rep code'
          caption='Advisors without an assigned code are grouped as “(unattributed).”'
          data={netNewAumByRia}
          loading={loading}
        />
        <NetByCategoryChart
          id='chart-net-new-aum-by-asset'
          title='Net New AUM by Asset'
          source='Net transfers per asset'
          caption='Same transfers as Money Movement by Asset above, netted rather than shown two-sided.'
          data={netNewAumByAsset}
          loading={loading}
        />
        <NetByCategoryChart
          id='chart-net-new-aum-by-fund'
          title='Net New AUM by Fund'
          source='Net transfers per dwp.subadvisor allocation model'
          caption='Accounts with no allocation model assigned are grouped as “(unassigned).”'
          data={netNewAumByFund}
          loading={loading}
        />
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Account Size
      </h2>
      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        {/* Two measures, deliberately distinguished. The solid area is real account
            value from custody snapshots — the metric this KPI is named after — but it
            only has a point per backfill run, so connectNulls is off and the line
            shows gaps rather than inventing values. The dashed line is cumulative
            deposits less withdrawals: it has daily history back to each account's
            first deposit, but it is contributed capital, so it ignores market
            movement entirely and must never be read as account value. */}
        <ChartCard
          id='chart-wealth-avg-account-size'
          className='lg:col-span-12'
          title='Average Account Size'
          source='Account value from custody snapshots; net funded = deposits less withdrawals'
          height={210}
          loading={loading}
          empty={byDate.length === 0}
          caption={
            latestValued
              ? `${latestValued.accountCount} accounts valued as of ${latestValued.day}`
              : latestFunded
                ? 'No custody value snapshot in range — net funded shown for context'
                : undefined
          }
        >
          <ResponsiveContainer width='100%' height={210}>
            <AreaChart data={byDate} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id='wealthTrendGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={BRAND} stopOpacity={0.35} />
                  <stop offset='100%' stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='day'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={28}
              />
              <YAxis {...cleanAxis} width={48} tick={axisTick} tickFormatter={fmtUsdCompact} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtUsdOrDash}
                cursor={{
                  stroke: 'var(--color-border-secondary)',
                  strokeDasharray: '3 3',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                iconType='plainline'
                iconSize={12}
              />
              <Area
                type='monotone'
                dataKey='avgMarketValueUsd'
                name='Avg account value'
                stroke={BRAND}
                strokeWidth={2}
                fill='url(#wealthTrendGradient)'
                connectNulls={false}
                dot={byDate.length <= 8 ? { r: 3, fill: BRAND, strokeWidth: 0 } : false}
                activeDot={{ r: 3.5, fill: BRAND, strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <Line
                type='monotone'
                dataKey='avgNetFundedUsd'
                name='Avg net funded'
                stroke={AXIS}
                strokeWidth={1.5}
                strokeDasharray='4 3'
                dot={false}
                activeDot={{ r: 3, fill: AXIS, strokeWidth: 0 }}
                connectNulls
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* USD is the default: 0.62 BTC and 15,200 USD share no meaningful
            scale, so quantity bars are flattened for low-unit-count assets.
            The toggle is opt-in — users who want the quantity view anyway
            (per business request) can switch to it; the other figure always
            rides along in the tooltip regardless of mode. */}
        <ChartCard
          className='lg:col-span-12'
          title='Average Holding by Token'
          source={
            tokenMetric === 'usd'
              ? 'Average per-account holding, by asset (USD value)'
              : 'Average per-account holding, by asset (token quantity)'
          }
          height={showTokenToggle && !tokenChartExpanded ? tokenCollapsedHeight : byTokenHeight}
          loading={loading}
          empty={byToken.length === 0}
          right={<UsdQtyToggle mode={tokenMetric} onChange={setTokenMetric} />}
          caption={
            showTokenToggle && (
              <button
                onClick={() => setTokenChartExpanded((e) => !e)}
                className='text-[11px] text-on-surface-secondary underline-offset-2 hover:underline'
              >
                {tokenChartExpanded ? 'Show fewer' : `Show all ${byToken.length} tokens`}
              </button>
            )
          }
        >
          <div
            style={{
              height: showTokenToggle && !tokenChartExpanded ? tokenCollapsedHeight : byTokenHeight,
              overflow: 'hidden',
            }}
          >
            <ResponsiveContainer width='100%' height={byTokenHeight}>
              <BarChart
                data={byToken}
                layout='vertical'
                margin={{ left: 8, right: 56, top: 4, bottom: 4 }}
              >
                <XAxis type='number' hide />
                <YAxis
                  type='category'
                  dataKey='token'
                  width={80}
                  {...cleanAxis}
                  tick={{ fill: AXIS, fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
                  formatter={(_value: any, _name: any, item: any) => {
                    const usdVal = item?.payload?.avgNotionalUsd;
                    const qty = item?.payload?.avgHolding;
                    const token = item?.payload?.token ?? '';
                    return tokenMetric === 'usd'
                      ? [`${fmtUsdOrDash(usdVal)} · ${fmtQty(qty)} ${token}`, 'Avg holding']
                      : [`${fmtQty(qty)} ${token} · ${fmtUsdOrDash(usdVal)}`, 'Avg holding'];
                  }}
                />
                <Bar
                  dataKey={tokenMetric === 'usd' ? 'avgNotionalUsd' : 'avgHolding'}
                  name='Avg holding'
                  fill={BRAND}
                  radius={[0, 5, 5, 0]}
                  barSize={15}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey={tokenMetric === 'usd' ? 'avgNotionalUsd' : 'avgHolding'}
                    position='right'
                    formatter={(v: any) =>
                      v == null ? '—' : tokenMetric === 'usd' ? fmtUsdCompact(v) : fmtQty(v)
                    }
                    style={{ fill: 'var(--color-on-surface-secondary)', fontSize: 11 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Daily Total AUM: unlike Average Account Size above, this is the
            SUM across every account, not the per-account average. Custodied
            = ties to every other Wealth panel (custody_scope <> heldaway_external);
            Total also includes externally-custodied balances (Coinbase/Gemini). */}
        <ChartCard
          id='chart-wealth-aum-trend'
          className='lg:col-span-12'
          title='Daily Total AUM'
          source='Custodied = Anchorage-custodied + advisor-reported; Total also includes externally-held balances'
          height={210}
          loading={loading}
          empty={aumTrend.length === 0}
          caption={latestAum ? `As of ${latestAum.bucket}` : undefined}
        >
          <ResponsiveContainer width='100%' height={210}>
            <AreaChart data={aumTrend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id='aumTrendGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={BRAND} stopOpacity={0.35} />
                  <stop offset='100%' stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='bucket'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={28}
              />
              <YAxis {...cleanAxis} width={48} tick={axisTick} tickFormatter={fmtUsdCompact} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtUsdOrDash}
                cursor={{ stroke: 'var(--color-border-secondary)', strokeDasharray: '3 3' }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                iconType='plainline'
                iconSize={12}
              />
              <Area
                type='monotone'
                dataKey='custodiedAumUsd'
                name='Custodied AUM'
                stroke={BRAND}
                strokeWidth={2}
                fill='url(#aumTrendGradient)'
                connectNulls={false}
                dot={aumTrend.length <= 8 ? { r: 3, fill: BRAND, strokeWidth: 0 } : false}
                activeDot={{ r: 3.5, fill: BRAND, strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <Line
                type='monotone'
                dataKey='totalAumUsd'
                name='Total AUM (incl. external)'
                stroke={AXIS}
                strokeWidth={1.5}
                strokeDasharray='4 3'
                dot={false}
                activeDot={{ r: 3, fill: AXIS, strokeWidth: 0 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          id='chart-wealth-aum-by-custodian'
          className='lg:col-span-6'
          title='AUM by Custodian'
          source='Current total AUM held at each custodian; externally-held custodians are outside the custodied subtotal'
          height={aumByCustodianHeight}
          loading={loading}
          empty={aumByCustodian.length === 0}
        >
          <table className='w-full border-collapse text-[12px]'>
            <thead>
              <tr className='border-b border-theme-border text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-on-surface-tertiary'>
                <th className='py-1.5 pr-2 font-semibold'>Custodian</th>
                <th className='py-1.5 pl-2 text-right font-semibold'>AUM</th>
              </tr>
            </thead>
            <tbody>
              {aumCustodiedRows.map((r) => (
                <tr
                  key={`${r.custodian}_${r.custodyScope}`}
                  className='border-b border-theme-border/60'
                >
                  <td className='py-1.5 pr-2 text-on-surface-secondary'>
                    {custodianLabel(r.custodian, r.custodyScope)}
                  </td>
                  <td className='py-1.5 pl-2 text-right tabular-nums text-on-surface'>
                    {fmtUsd(r.aumUsd)}
                  </td>
                </tr>
              ))}
              {aumExternalRows.length > 0 && (
                <tr className='border-b-2 border-theme-border font-semibold'>
                  <td className='py-1.5 pr-2 text-on-surface'>Custodied Subtotal</td>
                  <td className='py-1.5 pl-2 text-right tabular-nums text-on-surface'>
                    {fmtUsd(aumCustodiedSubtotal)}
                  </td>
                </tr>
              )}
              {aumExternalRows.map((r) => (
                <tr
                  key={`${r.custodian}_${r.custodyScope}`}
                  className='border-b border-theme-border/60'
                >
                  <td className='py-1.5 pr-2 italic text-on-surface-tertiary'>
                    {custodianLabel(r.custodian, r.custodyScope)} (external)
                  </td>
                  <td className='py-1.5 pl-2 text-right tabular-nums italic text-on-surface-tertiary'>
                    {fmtUsd(r.aumUsd)}
                  </td>
                </tr>
              ))}
              <tr className='font-semibold'>
                <td className='py-1.5 pr-2 text-on-surface'>
                  {aumExternalRows.length > 0 ? 'Grand Total' : 'Total'}
                </td>
                <td className='py-1.5 pl-2 text-right tabular-nums text-on-surface'>
                  {fmtUsd(aumGrandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </ChartCard>

        <ChartCard
          id='chart-wealth-aum-by-asset'
          className='lg:col-span-6'
          title='AUM by Asset'
          source={
            aumAssetMetric === 'usd'
              ? 'Current total AUM by asset; proxy strategy positions (e.g. XRP_TACTIC) are collapsed into their base asset'
              : 'Current total token quantity by asset; proxy strategy positions (e.g. XRP_TACTIC) are collapsed into their base asset'
          }
          height={
            showAumAssetToggle && !aumAssetChartExpanded
              ? aumAssetCollapsedHeight
              : aumByAssetHeight
          }
          loading={loading}
          empty={aumByAsset.length === 0}
          right={<UsdQtyToggle mode={aumAssetMetric} onChange={setAumAssetMetric} />}
          caption={
            showAumAssetToggle && (
              <button
                onClick={() => setAumAssetChartExpanded((e) => !e)}
                className='text-[11px] text-on-surface-secondary underline-offset-2 hover:underline'
              >
                {aumAssetChartExpanded ? 'Show fewer' : `Show all ${aumByAsset.length} assets`}
              </button>
            )
          }
        >
          <div
            style={{
              height:
                showAumAssetToggle && !aumAssetChartExpanded
                  ? aumAssetCollapsedHeight
                  : aumByAssetHeight,
              overflow: 'hidden',
            }}
          >
            <ResponsiveContainer width='100%' height={aumByAssetHeight}>
              <BarChart
                data={aumByAsset}
                layout='vertical'
                margin={{ left: 8, right: 56, top: 4, bottom: 4 }}
              >
                <XAxis type='number' hide />
                <YAxis
                  type='category'
                  dataKey='asset'
                  width={80}
                  {...cleanAxis}
                  tick={{ fill: AXIS, fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
                  formatter={(_value: any, _name: any, item: any) => {
                    const usdVal = item?.payload?.aumUsd;
                    const qty = item?.payload?.assetQty;
                    return aumAssetMetric === 'usd'
                      ? [fmtUsdOrDash(usdVal), 'AUM']
                      : [fmtQty(qty), 'AUM (qty)'];
                  }}
                />
                <Bar
                  dataKey={aumAssetMetric === 'usd' ? 'aumUsd' : 'assetQty'}
                  name='AUM'
                  fill={BRAND}
                  radius={[0, 5, 5, 0]}
                  barSize={15}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey={aumAssetMetric === 'usd' ? 'aumUsd' : 'assetQty'}
                    position='right'
                    formatter={(v: any) =>
                      v == null ? '—' : aumAssetMetric === 'usd' ? fmtUsdCompact(v) : fmtQty(v)
                    }
                    style={{ fill: 'var(--color-on-surface-secondary)', fontSize: 11 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Funded Accounts
      </h2>
      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-funded-accounts'
          className='lg:col-span-12'
          title='New Funded Accounts'
          source='Accounts with their first Anchorage deposit in each period'
          height={210}
          loading={loading}
          empty={fundedAccountsTrend.length === 0}
          caption='An account is counted once — in the period of its earliest DEPOSIT transaction.'
        >
          <CountTrendChart data={fundedAccountsTrend} />
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        RIAs Onboarded
      </h2>
      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-ria-onboarded'
          className='lg:col-span-12'
          title='New RIAs Onboarded'
          source='Subadvisor firms currently onboarded, by creation period'
          height={210}
          loading={loading}
          empty={riaOnboardedTrend.length === 0}
          caption='Firms are counted in the period they were created and are onboarded as of now — the source has no timestamp for when onboarding itself completed.'
        >
          <CountTrendChart data={riaOnboardedTrend} />
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        Client Meetings
      </h2>
      <section className='mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12'>
        <ChartCard
          id='chart-meetings-trend'
          className='lg:col-span-7'
          title='Client Meetings Trend'
          source='Completed advisor meetings per period'
          height={210}
          loading={loading}
          empty={meetingsTrend.length === 0}
        >
          <ResponsiveContainer width='100%' height={210}>
            <AreaChart data={meetingsTrend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id='meetingsGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor='#ba9965' stopOpacity={0.35} />
                  <stop offset='100%' stopColor='#ba9965' stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey='bucket'
                {...cleanAxis}
                tick={axisTick}
                interval='preserveStartEnd'
                minTickGap={28}
              />
              <YAxis {...cleanAxis} width={30} tick={axisTick} allowDecimals={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtCount}
                cursor={{
                  stroke: 'var(--color-border-secondary)',
                  strokeDasharray: '3 3',
                }}
              />
              <Area
                type='monotone'
                dataKey='meetings'
                stroke='#ba9965'
                strokeWidth={2}
                fill='url(#meetingsGradient)'
                dot={meetingsTrend.length <= 8 ? { r: 3, fill: '#ba9965', strokeWidth: 0 } : false}
                activeDot={{ r: 3.5, fill: '#ba9965', strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          className='lg:col-span-5'
          title='Meetings by Advisor'
          source='Completed meetings by advisor, in range'
          height={byAdvisorHeight}
          loading={loading}
          empty={meetingsByAdvisor.length === 0}
        >
          <ResponsiveContainer width='100%' height={byAdvisorHeight}>
            <BarChart
              data={meetingsByAdvisor}
              layout='vertical'
              margin={{ left: 8, right: 40, top: 4, bottom: 4 }}
            >
              <XAxis type='number' hide allowDecimals={false} />
              <YAxis
                type='category'
                dataKey='advisor'
                width={140}
                {...cleanAxis}
                tick={{ fill: AXIS, fontSize: 11 }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={fmtCount}
                cursor={{ fill: 'var(--color-surface-tertiary)', opacity: 0.5 }}
              />
              <Bar
                dataKey='meetings'
                fill={BRAND}
                radius={[0, 5, 5, 0]}
                barSize={15}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey='meetings'
                  position='right'
                  style={{
                    fill: 'var(--color-on-surface-secondary)',
                    fontSize: 11,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <h2
        className='mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]'
        style={{ color: 'var(--color-primary)' }}
      >
        All Wealth & AUM KPIs
      </h2>
      <KpiChipGrid area='Wealth & AUM' />
    </>
  );
};

// ── non-revenue area view ────────────────────────────────────────────────────

const KpiChipGrid: React.FC<{ area: string }> = ({ area }) => {
  const group = kpiThemes.find((t) => t.theme === area);
  const kpis = group?.kpis ?? [];
  return (
    <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
      {kpis.map((k) => (
        <KpiChip key={k.name} kpi={k} />
      ))}
    </div>
  );
};

const AreaView: React.FC<{ area: string }> = ({ area }) => {
  const group = kpiThemes.find((t) => t.theme === area);
  const kpis = group?.kpis ?? [];
  const dataAvailable = kpis.filter((k) => hasData(k.status)).length;
  return (
    <>
      <header className='mb-5'>
        <div
          className='mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]'
          style={{ color: 'var(--color-primary)' }}
        >
          Business Area
        </div>
        <h1
          className='text-4xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: SERIF }}
        >
          {area}
        </h1>
        <p className='mt-2 text-sm text-on-surface-secondary'>
          {kpis.length} KPIs tracked · {dataAvailable} data available
        </p>
      </header>
      <div
        className='mb-6 rounded-xl border border-theme-border bg-surface p-4 text-[13px] text-on-surface-secondary'
        style={{ boxShadow: 'var(--color-card-edge)' }}
      >
        Live charts for <strong>{area}</strong> will appear here as its data sources connect. Below
        are the metrics planned for this area.
      </div>
      <KpiChipGrid area={area} />
    </>
  );
};
